/**
 * Ajustar a Ruth: onde a mãe desdiz o que falou no onboarding.
 *
 * **O nome da tela é o do menu.** O mesmo lugar se chamava "Ajustar a Ruth" no
 * Mais, "Configurações" aqui dentro e "as configurações" numa frase da Semana:
 * a mãe tocava num nome e chegava noutro, sem saber se era ali mesmo. Venceu o
 * nome que ela lê primeiro, que é também o único dos três que soa como gente —
 * "configurações" é palavra de painel, e a Ruth não é painel. O arquivo, a rota
 * e o CSS continuam `configuracoes`: isso é endereço, não nome.
 *
 * O onboarding é conversa, e conversa erra — ela diz "sete da manhã" com o
 * bebê no colo e três dias depois descobre que às sete ainda está dormindo.
 * Esta é a única tela do produto onde ela mexe num campo em vez de falar, e
 * existe só para isso: corrigir sem precisar explicar.
 *
 * **Não tem botão de salvar.** Ela mexe e está feito. Por isso a tela mostra
 * sempre o que o servidor respondeu, nunca o que ela clicou — e quando o
 * servidor recusa, a frase aparece embaixo do campo que causou, com o valor
 * dela ainda lá para corrigir. Voltar o campo sozinho seria a Ruth discutindo.
 *
 * **A janela de contato não é "notificações".** São as horas entre as quais a
 * Ruth aceita interromper — silêncio é funcionalidade, e este é o controle.
 *
 * **A capacidade descreve o dia, não a mãe.** Nada aqui mede ninguém: leve,
 * normal e pesado dizem como o dia costuma ser, e o gerador para de empilhar
 * tarefa em dia pesado. A tela diz isso com todas as letras.
 *
 * Não existe mockup desta tela em docs/design. A linguagem de lista com chave
 * vem de Casa.dc.html; o resto é desenho novo, para o Carlos revisar.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  fraseDoErro,
  diaDaCasa,
  ErroDaApi,
  type Configuracoes as Ajustes,
  type ComodoDaCasa,
  type CorrecaoDeComodo,
  type CorrecaoDePessoa,
  type MembroDaCasa,
  type PapelDeMembro,
  type TipoDeComodo,
  type MudancaNasConfiguracoes as Mudanca,
} from '../api'
import { Bloco, FormularioDoBloco } from '../componentes/BlocoDoDia'
import { Icone } from '../componentes/Icone'
import { Integracoes } from '../componentes/Integracoes'
import {
  desligar,
  estado as lerAvisos,
  ligar,
  suportado as temPush,
  type EstadoDosAvisos,
} from '../push'
import '../estilos/configuracoes.css'

const DIAS = [
  { sigla: 'seg', nome: 'Segunda', curto: 'seg' },
  { sigla: 'ter', nome: 'Terça', curto: 'ter' },
  { sigla: 'qua', nome: 'Quarta', curto: 'qua' },
  { sigla: 'qui', nome: 'Quinta', curto: 'qui' },
  { sigla: 'sex', nome: 'Sexta', curto: 'sex' },
  { sigla: 'sab', nome: 'Sábado', curto: 'sáb' },
  { sigla: 'dom', nome: 'Domingo', curto: 'dom' },
]

const PESOS = [
  { valor: 'leve', rotulo: 'Leve' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'pesado', rotulo: 'Pesado' },
]

const PAPEIS: Record<string, string> = {
  mae: 'mãe',
  pai: 'pai',
  crianca: 'criança',
  ajudante: 'ajuda em casa',
  outro: 'mora com você',
}

/**
 * Os cômodos, como a mãe os chama.
 *
 * A ordem é a de uma casa: onde se cozinha, onde se lava, onde se dorme, e
 * depois o que nem toda casa tem.
 */
const TIPOS_DE_COMODO: { valor: TipoDeComodo; rotulo: string }[] = [
  { valor: 'cozinha', rotulo: 'cozinha' },
  { valor: 'banheiro', rotulo: 'banheiro' },
  { valor: 'quarto', rotulo: 'quarto' },
  { valor: 'sala', rotulo: 'sala' },
  { valor: 'lavanderia', rotulo: 'lavanderia' },
  { valor: 'area_servico', rotulo: 'área de serviço' },
  { valor: 'quintal', rotulo: 'quintal' },
  { valor: 'varanda', rotulo: 'varanda' },
  { valor: 'escritorio', rotulo: 'escritório' },
  { valor: 'outro', rotulo: 'outro' },
]

/**
 * O que a mãe escolhe. É o mesmo `papel`, dito como ela diria.
 *
 * Cinco opções não descrevem uma família — sogra, madrinha, enteado, a irmã que
 * ficou uns meses. Quem diz isso é o campo "como você chama", em texto livre;
 * aqui embaixo fica só o que o sistema precisa entender para se comportar:
 * criança tem bloco protegido e toma remédio, mãe recebe o resumo da manhã,
 * ajudante tem dia de faxina.
 */
const ESCOLHAS_DE_PAPEL: { valor: PapelDeMembro; rotulo: string }[] = [
  { valor: 'crianca', rotulo: 'criança' },
  { valor: 'mae', rotulo: 'mãe' },
  { valor: 'pai', rotulo: 'pai' },
  { valor: 'ajudante', rotulo: 'ajuda em casa' },
  { valor: 'outro', rotulo: 'outra pessoa' },
]

/** Qual campo estragou, para a frase aparecer onde ela mexeu. */
type Recusa = { campos: string[]; frase: string }

export function Configuracoes() {
  const [ajustes, setAjustes] = useState<Ajustes | null>(null)
  /** O id de quem ela está corrigindo, ou 'nova' para alguém que falta. */
  const [corrigindo, setCorrigindo] = useState<string | null>(null)
  /** O mesmo, para os cômodos: um id, ou 'novo'. */
  const [comodoAberto, setComodoAberto] = useState<string | null>(null)
  /** E para os blocos do dia. */
  const [blocoAberto, setBlocoAberto] = useState<string | null>(null)
  const [recusa, setRecusa] = useState<Recusa | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)
  const [anotado, setAnotado] = useState(false)

  const carregar = useCallback(async () => {
    try {
      setAjustes(await api.configuracoes())
      setAberto(null)
    } catch (e) {
      setAberto(fraseDoErro(e, 'não consegui abrir suas configurações'))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // O "anotei" some sozinho: é confirmação, não histórico.
  useEffect(() => {
    if (!anotado) return
    const conta = setTimeout(() => setAnotado(false), 2000)
    return () => clearTimeout(conta)
  }, [anotado])

  const mudar = useCallback(async (mudanca: Mudanca) => {
    try {
      setAjustes(await api.mudarConfiguracoes(mudanca))
      setRecusa(null)
      setAnotado(true)
    } catch (e) {
      setRecusa({
        campos: Object.keys(mudanca),
        frase: fraseDoErro(e, 'não consegui mudar isso agora'),
      })
    }
  }, [])

  /**
   * As listas da casa — quem mora nela e os cômodos — fecham o formulário e
   * recarregam com o que o servidor diz, e mostram a recusa em frase de gente
   * como os outros campos desta tela.
   *
   * `onde` diz debaixo de qual lista a frase sai. Sem isso, "Já tem alguém
   * chamado Ana aqui" apareceria também embaixo dos cômodos, onde não quer
   * dizer nada.
   */
  const naLista = useCallback(async (onde: string, chamada: Promise<Ajustes>) => {
    try {
      setAjustes(await chamada)
      setCorrigindo(null)
      setComodoAberto(null)
      setBlocoAberto(null)
      setRecusa(null)
      setAnotado(true)
    } catch (e) {
      setRecusa({
        campos: [onde],
        frase: fraseDoErro(e, 'não consegui mexer nisso agora'),
      })
    }
  }, [])

  const mexerNoBloco = useCallback(
    (chamada: Promise<Ajustes>) => naLista('blocos', chamada),
    [naLista],
  )

  const mexerNoComodo = useCallback(
    (chamada: Promise<Ajustes>) => naLista('comodos', chamada),
    [naLista],
  )

  const corrigirPessoa = useCallback(
    (membro: string, campos: CorrecaoDePessoa) =>
      naLista('membros', api.corrigirPessoa(membro, campos)),
    [naLista],
  )

  const acrescentarPessoa = useCallback(
    (campos: CorrecaoDePessoa) =>
      naLista(
        'membros',
        api.acrescentarPessoa({
          ...campos,
          nome: campos.nome ?? '',
          papel: campos.papel ?? 'outro',
        }),
      ),
    [naLista],
  )

  /** A frase da recusa só aparece embaixo do campo que a causou. */
  const recusaDe = (...campos: string[]) =>
    recusa && recusa.campos.some((campo) => campos.includes(campo)) ? recusa.frase : null

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Ajustar a Ruth</h1>
          <span className="cabeca__data">o que você me contou no começo</span>
        </div>
        <span
          className={`conf-anotado ${anotado ? 'conf-anotado--visivel' : ''}`}
          aria-live="polite"
        >
          {anotado ? 'anotei' : ''}
        </span>
      </header>

      <div className="corpo">
        {aberto && <p className="vazio">{aberto}</p>}
        {!ajustes && !aberto && <p className="vazio">Um instante.</p>}

        {ajustes && (
          <>
            <section className="conf-secao">
              <h2 className="conf-secao__titulo">Como eu te chamo</h2>
              <Nome
                valor={ajustes.nome_tratamento}
                aoMudar={(nome) => void mudar({ nome_tratamento: nome })}
              />
              <Frase texto={recusaDe('nome_tratamento')} />
            </section>

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">Quando eu posso falar</h2>
              <p className="conf-secao__nota">
                Fora dessas horas eu fico quieta. Só remédio na hora atravessa.
              </p>

              <Hora
                nome="Meu resumo de manhã"
                dica="o dia inteiro numa mensagem só"
                valor={ajustes.resumo_hora}
                aoMudar={(hora) => void mudar({ resumo_hora: hora })}
              />
              <Hora
                nome="Depois disso, silêncio"
                dica="não te procuro mais até o resumo do dia seguinte"
                valor={ajustes.silencio_inicio}
                aoMudar={(hora) => void mudar({ silencio_inicio: hora })}
              />
              <Frase texto={recusaDe('resumo_hora', 'silencio_inicio')} />

              <Chave
                nome="Uma palavra no fim do dia"
                dica="curta, sobre o dia que passou, antes do silêncio"
                ligada={ajustes.fechamento_noturno}
                aoVirar={(liga) => void mudar({ fechamento_noturno: liga })}
              />
              <Frase texto={recusaDe('fechamento_noturno')} />
            </section>

            <AvisosNoCelular />

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">O dia da casa</h2>
              <p className="conf-secao__nota">
                Não marco nada antes de vocês acordarem nem depois de dormirem.
              </p>

              <Hora
                nome="A casa acorda"
                valor={ajustes.acorda}
                aoMudar={(hora) => void mudar({ acorda: hora })}
              />
              <Hora
                nome="A casa dorme"
                valor={ajustes.dorme}
                aoMudar={(hora) => void mudar({ dorme: hora })}
              />
              <Frase texto={recusaDe('acorda', 'dorme')} />
            </section>

            <section className="conf-secao">
              {/* "Quem te ajuda" prometia uma lista de pessoas e entregava uma
                  chave e sete dias. "Ajuda em casa" é como a própria tela já
                  chama isso na lista de quem mora aqui, e é o que a seção tem. */}
              <h2 className="conf-secao__titulo">Ajuda em casa</h2>

              <Chave
                nome="Tenho ajuda em casa"
                dica="nos dias dela eu deixo o que é mais pesado"
                ligada={ajustes.tem_ajudante}
                aoVirar={(liga) => void mudar({ tem_ajudante: liga })}
              />

              {ajustes.tem_ajudante && (
                <>
                  <p className="conf-secao__nota">Que dias ela vem</p>
                  <div className="conf-dias">
                    {DIAS.map((dia) => {
                      const vem = ajustes.dias_ajudante.includes(dia.sigla)
                      return (
                        <button
                          key={dia.sigla}
                          type="button"
                          className={`conf-dia ${vem ? 'conf-dia--marcado' : ''}`}
                          aria-pressed={vem}
                          aria-label={dia.nome}
                          onClick={() =>
                            void mudar({
                              dias_ajudante: vem
                                ? ajustes.dias_ajudante.filter((s) => s !== dia.sigla)
                                : [...ajustes.dias_ajudante, dia.sigla],
                            })
                          }
                        >
                          {dia.curto}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
              <Frase texto={recusaDe('tem_ajudante', 'dias_ajudante')} />
            </section>

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">Como costuma ser cada dia</h2>
              <p className="conf-secao__nota">
                Isso descreve o dia, não você. Em dia pesado eu não empilho tarefa.
              </p>

              {DIAS.map((dia) => (
                <div key={dia.sigla} className="conf-semana__linha">
                  <span className="conf-semana__dia">{dia.nome}</span>
                  <div className="conf-peso" role="group" aria-label={dia.nome}>
                    {PESOS.map((peso) => {
                      const atual = (ajustes.capacidade_por_dia[dia.sigla] ?? 'normal') === peso.valor
                      return (
                        <button
                          key={peso.valor}
                          type="button"
                          className={`conf-peso__opcao ${atual ? 'conf-peso__opcao--atual' : ''}`}
                          aria-pressed={atual}
                          onClick={() =>
                            void mudar({
                              capacidade_por_dia: { [dia.sigla]: peso.valor },
                            })
                          }
                        >
                          {peso.rotulo}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
              <Frase texto={recusaDe('capacidade_por_dia')} />
            </section>

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">Quem mora com você</h2>
              {ajustes.membros.map((membro) => (
                <Pessoa
                  key={membro.id}
                  membro={membro}
                  corrigindo={corrigindo === membro.id}
                  aoCorrigir={() => setCorrigindo(membro.id)}
                  aoFechar={() => setCorrigindo(null)}
                  aoSalvar={(campos) => corrigirPessoa(membro.id, campos)}
                />
              ))}

              {corrigindo === 'nova' && (
                <FormularioDaPessoa
                  aoSalvar={(campos) => acrescentarPessoa(campos)}
                  aoFechar={() => setCorrigindo(null)}
                />
              )}

              {/* Com um formulário aberto, o convite some: ela está no meio de
                  uma coisa, e "é só me falar" ali vira ruído. */}
              {corrigindo === null && (
                <>
                  <p className="conf-secao__nota">Pra mudar alguém aqui, é só me falar.</p>
                  <button
                    type="button"
                    className="conf-membro__acrescentar"
                    onClick={() => setCorrigindo('nova')}
                  >
                    Se eu não conseguir, acrescente aqui
                  </button>
                </>
              )}
              <Frase texto={recusaDe('membros')} />
            </section>

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">O seu dia</h2>
              <p className="conf-secao__nota">
                É entre uma coisa e outra daqui que eu encaixo o resto. O que está marcado com
                cadeado eu não atravesso: nem tarefa, nem aviso.
              </p>

              {ajustes.blocos.map((bloco) => (
                <Bloco
                  key={bloco.id}
                  bloco={bloco}
                  corrigindo={blocoAberto === bloco.id}
                  aoCorrigir={() => setBlocoAberto(bloco.id)}
                  aoFechar={() => setBlocoAberto(null)}
                  aoSalvar={(campos) => mexerNoBloco(api.corrigirBloco(bloco.id, campos))}
                  aoTirar={() => mexerNoBloco(api.tirarBloco(bloco.id))}
                />
              ))}

              {blocoAberto === 'novo' ? (
                <FormularioDoBloco
                  aoSalvar={(campos) =>
                    mexerNoBloco(
                      api.acrescentarBloco({
                        tipo: campos.tipo ?? 'outro',
                        descricao: campos.descricao ?? null,
                        dias: campos.dias ?? ['seg', 'ter', 'qua', 'qui', 'sex'],
                        inicio: campos.inicio ?? '09:00:00',
                        fim: campos.fim ?? '10:00:00',
                      }),
                    )
                  }
                  aoFechar={() => setBlocoAberto(null)}
                />
              ) : (
                blocoAberto === null && (
                  <button
                    type="button"
                    className="conf-membro__acrescentar"
                    onClick={() => setBlocoAberto('novo')}
                  >
                    Acrescentar uma parte do dia
                  </button>
                )
              )}
              <Frase texto={recusaDe('blocos')} />
            </section>

            <section className="conf-secao">
              <h2 className="conf-secao__titulo">Os cômodos</h2>
              <p className="conf-secao__nota">
                É por aqui que eu sei o que tem pra limpar. Um banheiro a mais na lista vira
                uma faxina a mais na sua semana.
              </p>

              {ajustes.comodos.map((comodo) => (
                <Comodo
                  key={comodo.id}
                  comodo={comodo}
                  corrigindo={comodoAberto === comodo.id}
                  aoCorrigir={() => setComodoAberto(comodo.id)}
                  aoFechar={() => setComodoAberto(null)}
                  aoSalvar={(campos) => mexerNoComodo(api.corrigirComodo(comodo.id, campos))}
                  aoTirar={() => mexerNoComodo(api.tirarComodo(comodo.id))}
                />
              ))}

              {comodoAberto === 'novo' ? (
                <FormularioDoComodo
                  aoSalvar={(campos) =>
                    mexerNoComodo(
                      api.acrescentarComodo({
                        tipo: campos.tipo ?? 'outro',
                        nome: campos.nome ?? '',
                      }),
                    )
                  }
                  aoFechar={() => setComodoAberto(null)}
                />
              ) : (
                comodoAberto === null && (
                  <button
                    type="button"
                    className="conf-membro__acrescentar"
                    onClick={() => setComodoAberto('novo')}
                  >
                    Acrescentar um cômodo
                  </button>
                )
              )}
              <Frase texto={recusaDe('comodos')} />
            </section>

            <section className="conf-secao conf-secao--dados">
              <h2 className="conf-secao__titulo">
                <Icone nome="protegido" tamanho={18} />
                Seus dados
              </h2>
              {ajustes.consentimento_lgpd_em ? (
                <p className="conf-secao__nota">
                  Em {emDataLonga(ajustes.consentimento_lgpd_em)} você me autorizou a guardar receita
                  e remédio das crianças. Se quiser que eu apague alguma coisa, é só me pedir.
                </p>
              ) : (
                <p className="conf-secao__nota">
                  Ainda não guardo receita nem remédio das crianças. Quando precisar, eu te peço
                  antes.
                </p>
              )}
              <p className="conf-secao__nota">
                Todo horário desta tela é o relógio da sua casa.
                <span className="conf-fuso">{ajustes.timezone}</span>
              </p>
            </section>

            <section className="conf-secao">
              <Integracoes />
            </section>
          </>
        )}
      </div>
    </>
  )
}

/** A frase da recusa, embaixo do campo. Frase, nunca alerta. */
function Frase({ texto }: { texto: string | null }) {
  if (!texto) return null
  return (
    <p className="conf-erro" role="status">
      {texto}
    </p>
  )
}

/**
 * O nome de tratamento. Grava ao sair do campo: enquanto ela digita "Dani" a
 * Ruth não a chama de "D", "Da", "Dan".
 */
function Nome({ valor, aoMudar }: { valor: string; aoMudar: (nome: string) => void }) {
  const [texto, setTexto] = useState(valor)

  useEffect(() => setTexto(valor), [valor])

  return (
    <input
      className="conf-campo"
      type="text"
      value={texto}
      maxLength={60}
      autoComplete="off"
      aria-label="Como eu te chamo"
      onChange={(evento) => setTexto(evento.target.value)}
      onBlur={() => {
        if (texto !== valor) aoMudar(texto)
      }}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') evento.currentTarget.blur()
      }}
    />
  )
}

/**
 * Uma hora da casa. O valor de tela é local enquanto ela mexe no relógio do
 * aparelho; o valor de verdade é o que voltou do servidor.
 */
function Hora({
  nome,
  dica,
  valor,
  aoMudar,
}: {
  nome: string
  dica?: string
  valor: string
  aoMudar: (hora: string) => void
}) {
  const [texto, setTexto] = useState(valor.slice(0, 5))

  useEffect(() => setTexto(valor.slice(0, 5)), [valor])

  return (
    <label className="conf-linha">
      <span className="conf-linha__texto">
        <span className="conf-linha__nome">{nome}</span>
        {dica && <span className="conf-linha__dica">{dica}</span>}
      </span>
      <input
        className="conf-hora"
        type="time"
        value={texto}
        onChange={(evento) => {
          setTexto(evento.target.value)
          // Meio-horário ("06:") não é hora: só vai quando está inteira.
          if (evento.target.value.length === 5) aoMudar(evento.target.value)
        }}
      />
    </label>
  )
}

/** A mesma chave da tela A casa: liga e desliga, sem confirmação. */
function Chave({
  nome,
  dica,
  ligada,
  travada = false,
  aoVirar,
}: {
  nome: string
  dica?: string
  ligada: boolean
  /** Enquanto o navegador ainda está respondendo, ou quando ele já disse não. */
  travada?: boolean
  aoVirar: (ligada: boolean) => void
}) {
  return (
    <div className="conf-linha">
      <span className="conf-linha__texto">
        <span className="conf-linha__nome">{nome}</span>
        {dica && <span className="conf-linha__dica">{dica}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={ligada}
        aria-label={nome}
        disabled={travada}
        className={`chave ${ligada ? 'chave--ligada' : ''} ${travada ? 'conf-chave--travada' : ''}`}
        onClick={() => aoVirar(!ligada)}
      >
        <span className="chave__bolinha" />
      </button>
    </div>
  )
}

/**
 * Os avisos que chegam com o app fechado.
 *
 * Mora aqui e não em "Quando eu posso falar" porque responde outra pergunta:
 * aquela seção diz *quando* a Ruth pode interromper, esta diz *por onde*. A
 * mãe pode ter combinado o resumo das sete e ainda assim não receber nada, se
 * o aparelho nunca foi inscrito — e não haveria onde descobrir isso.
 *
 * **É por aparelho, não por conta.** Ligar no celular não liga no notebook, e
 * a chave reflete só o aparelho que está na mão dela. A dica diz isso, porque
 * senão a chave desligada no notebook parece que ela perdeu o do celular.
 *
 * **Os três becos sem saída são ditos, não escondidos.** Navegador que não faz
 * push, iPhone fora da tela inicial, permissão já negada: em nenhum deles um
 * botão resolveria, e um botão que não faz nada é pior que nenhum botão.
 */
function AvisosNoCelular() {
  const [avisos, setAvisos] = useState<EstadoDosAvisos | null>(null)
  const [mexendo, setMexendo] = useState(false)
  const [frase, setFrase] = useState<string | null>(null)

  useEffect(() => {
    void lerAvisos().then(setAvisos)
  }, [])

  const virar = useCallback(async (liga: boolean) => {
    setMexendo(true)
    setFrase(null)
    try {
      setAvisos(await (liga ? ligar() : desligar()))
    } catch (e) {
      setFrase(
        e instanceof ErroDaApi && e.status === 503
          ? 'Ainda não sei mandar aviso nesta instalação. Aqui dentro eu continuo te contando tudo.'
          : e instanceof Error
            ? e.message
            : 'não consegui mexer nisso agora',
      )
    } finally {
      setMexendo(false)
    }
  }, [])

  // Nada aparece antes de saber: uma chave desligada por um instante e ligada
  // no instante seguinte faz a mãe achar que mexeu em alguma coisa.
  if (!avisos) return null

  return (
    <section className="conf-secao">
      <h2 className="conf-secao__titulo">Avisos no celular</h2>
      <p className="conf-secao__nota">
        É assim que eu te chamo na hora da dose e do compromisso, mesmo com o app fechado.
      </p>

      {!temPush() ? (
        <p className="conf-secao__nota">
          Este navegador não faz aviso fora do app. Aqui dentro eu continuo te contando tudo.
        </p>
      ) : avisos.faltaInstalarNoIphone ? (
        <p className="conf-instalar">
          No iPhone só funciona com o app adicionado à tela inicial (iOS 16.4+). Toque em
          compartilhar e depois em "Adicionar à Tela de Início".
        </p>
      ) : (
        <>
          <Chave
            nome="Me avisar neste aparelho"
            dica="vale só para o aparelho que você está usando agora"
            ligada={avisos.ligado}
            travada={mexendo || avisos.permissao === 'negada'}
            aoVirar={(liga) => void virar(liga)}
          />
          {avisos.permissao === 'negada' && (
            <p className="conf-secao__nota">
              Este navegador está bloqueando meus avisos. Dá para liberar nas configurações dele,
              em notificações deste site.
            </p>
          )}
        </>
      )}

      <Frase texto={frase} />
    </section>
  )
}

function Pessoa({
  membro,
  corrigindo,
  aoCorrigir,
  aoFechar,
  aoSalvar,
}: {
  membro: MembroDaCasa
  corrigindo: boolean
  aoCorrigir: () => void
  aoFechar: () => void
  aoSalvar: (campos: CorrecaoDePessoa) => Promise<void>
}) {
  // O que ela escreveu manda. "Sogra" é o que a mãe reconhece; "mora com você"
  // para uma sogra é a Ruth dizendo que não entendeu quem é aquela pessoa.
  const papel = membro.parentesco ?? PAPEIS[membro.papel] ?? membro.papel
  const idade = membro.data_nascimento ? emAnos(membro.data_nascimento) : null

  if (corrigindo) {
    return <FormularioDaPessoa membro={membro} aoSalvar={aoSalvar} aoFechar={aoFechar} />
  }

  return (
    <button type="button" className="conf-membro" onClick={aoCorrigir}>
      <span className="conf-membro__icone">
        <Icone nome="pessoa" tamanho={18} />
      </span>
      <span className="conf-membro__nome">{membro.nome}</span>
      <span className="conf-membro__papel">{idade ? `${papel} · ${idade}` : papel}</span>
    </button>
  )
}

/**
 * O formulário de uma pessoa — o mesmo para consertar e para acrescentar.
 *
 * Sem `membro` ele abre em branco, para quem a Ruth não conseguiu pôr na casa.
 * Com `membro`, vem preenchido: quase sempre ela quer mexer numa coisa só, e
 * em branco teria que redigitar o que já estava certo.
 */
function FormularioDaPessoa({
  membro,
  aoSalvar,
  aoFechar,
}: {
  membro?: MembroDaCasa
  aoSalvar: (campos: CorrecaoDePessoa) => Promise<void>
  aoFechar: () => void
}) {
  const [nome, setNome] = useState(membro?.nome ?? '')
  const [papel, setPapel] = useState<PapelDeMembro>(
    (membro?.papel as PapelDeMembro) ?? 'outro',
  )
  const [parentesco, setParentesco] = useState(membro?.parentesco ?? '')
  const [anos, setAnos] = useState(anosDe(membro?.data_nascimento ?? null))
  const [meses, setMeses] = useState(mesesDe(membro?.data_nascimento ?? null))
  const [salvando, setSalvando] = useState(false)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    const campos: CorrecaoDePessoa = {}
    if (nome.trim() && nome.trim() !== membro?.nome) campos.nome = nome.trim()
    if (papel !== membro?.papel) campos.papel = papel
    if ((parentesco.trim() || null) !== (membro?.parentesco ?? null))
      campos.parentesco = parentesco.trim() || null

    // Meses manda quando ela preencheu, porque é o que ela usa para bebê. Os
    // dois vão juntos: mandar só um deixaria o outro com o valor antigo.
    if (anos !== anosDe(membro?.data_nascimento ?? null) || meses !== mesesDe(membro?.data_nascimento ?? null)) {
      campos.idade_anos = anos ? Number(anos) : null
      campos.idade_meses = meses ? Number(meses) : null
    }

    if (!membro && !campos.nome) {
      aoFechar()
      return
    }
    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }

    setSalvando(true)
    await aoSalvar(campos)
    setSalvando(false)
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <label className="conf-pessoa__campo">
        <span>Nome</span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
      </label>

      <label className="conf-pessoa__campo">
        <span>Quem é</span>
        <select value={papel} onChange={(e) => setPapel(e.target.value as PapelDeMembro)}>
          {ESCOLHAS_DE_PAPEL.map(({ valor, rotulo }) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="conf-pessoa__campo">
        <span>Como você chama</span>
        <input
          value={parentesco}
          onChange={(e) => setParentesco(e.target.value)}
          placeholder="sogra, avó, madrinha"
          maxLength={40}
        />
      </label>

      <div className="conf-pessoa__idade">
        <label className="conf-pessoa__campo">
          <span>Idade</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={120}
            value={anos}
            onChange={(e) => setAnos(e.target.value)}
          />
        </label>
        <label className="conf-pessoa__campo">
          <span>Ou, se for bebê, meses</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={36}
            value={meses}
            onChange={(e) => setMeses(e.target.value)}
          />
        </label>
      </div>

      <div className="conf-pessoa__acoes">
        <button type="submit" className="botao-primario" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Deixa como está
        </button>
      </div>
    </form>
  )
}

/**
 * Um cômodo da casa. Tocar abre o conserto, como nas pessoas.
 *
 * A lista existe porque o gerador multiplica o catálogo por ela: "Limpar
 * Banheiro", "Limpar Banheiro 2". Um banheiro que a primeira conversa entendeu
 * a mais é uma faxina a mais na semana dela, para sempre, e até esta tela não
 * havia como ver isso — muito menos consertar.
 */
function Comodo({
  comodo,
  corrigindo,
  aoCorrigir,
  aoFechar,
  aoSalvar,
  aoTirar,
}: {
  comodo: ComodoDaCasa
  corrigindo: boolean
  aoCorrigir: () => void
  aoFechar: () => void
  aoSalvar: (campos: CorrecaoDeComodo) => Promise<void>
  aoTirar: () => Promise<void>
}) {
  const tipo = TIPOS_DE_COMODO.find((t) => t.valor === comodo.tipo)?.rotulo ?? comodo.tipo

  if (corrigindo) {
    return (
      <FormularioDoComodo
        comodo={comodo}
        aoSalvar={aoSalvar}
        aoFechar={aoFechar}
        aoTirar={aoTirar}
      />
    )
  }

  return (
    <button type="button" className="conf-membro" onClick={aoCorrigir}>
      <span className="conf-membro__icone">
        <Icone nome="casa" tamanho={18} />
      </span>
      <span className="conf-membro__nome">{comodo.nome}</span>
      <span className="conf-membro__papel">{tipo}</span>
    </button>
  )
}

/**
 * O formulário de um cômodo — o mesmo para consertar e para acrescentar.
 *
 * "Tirar da casa" pede confirmação porque leva junto as tarefas que só existiam
 * por causa dele. Não é um alerta de sistema: é a frase dizendo o que vai
 * acontecer, e o que **não** vai — o que ela já fez fica.
 */
function FormularioDoComodo({
  comodo,
  aoSalvar,
  aoFechar,
  aoTirar,
}: {
  comodo?: ComodoDaCasa
  aoSalvar: (campos: CorrecaoDeComodo) => Promise<void>
  aoFechar: () => void
  aoTirar?: () => Promise<void>
}) {
  const [tipo, setTipo] = useState<TipoDeComodo>(comodo?.tipo ?? 'outro')
  const [nome, setNome] = useState(comodo?.nome ?? '')
  const [perguntando, setPerguntando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    const campos: CorrecaoDeComodo = {}
    if (tipo !== comodo?.tipo) campos.tipo = tipo
    if (nome.trim() && nome.trim() !== comodo?.nome) campos.nome = nome.trim()

    if (Object.keys(campos).length === 0 || (!comodo && !campos.nome)) {
      aoFechar()
      return
    }

    setSalvando(true)
    await aoSalvar(campos)
    setSalvando(false)
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeComodo)}>
          {TIPOS_DE_COMODO.map(({ valor, rotulo }) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="conf-pessoa__campo">
        <span>Como você chama</span>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Banheiro das meninas"
          maxLength={80}
        />
      </label>

      {perguntando ? (
        <>
          <p className="conf-secao__nota">
            Tiro {comodo?.nome} da casa e paro de pedir o que era só dele. O que você já fez
            fica.
          </p>
          <div className="conf-pessoa__acoes">
            <button
              type="button"
              className="conf-pessoa__tirar"
              onClick={() => void aoTirar?.()}
            >
              Pode tirar
            </button>
            <button
              type="button"
              className="conf-pessoa__deixar"
              onClick={() => setPerguntando(false)}
            >
              Deixa aí
            </button>
          </div>
        </>
      ) : (
        <div className="conf-pessoa__acoes">
          <button type="submit" className="botao-primario" disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
            Deixa como está
          </button>
          {aoTirar && (
            <button
              type="button"
              className="conf-pessoa__tirar-link"
              onClick={() => setPerguntando(true)}
            >
              Não tenho esse cômodo
            </button>
          )}
        </div>
      )}
    </form>
  )
}

/**
 * Quantos anos, do jeito que o campo espera: texto, e vazio quando não dá.
 *
 * Bebê devolve vazio de propósito: quem tem oito meses tem zero ano, e um
 * campo mostrando "0" faria a mãe salvar uma criança recém-nascida.
 */
function anosDe(nascimento: string | null): string {
  const anos = emAnosInteiros(nascimento)
  return anos !== null && anos >= 1 ? String(anos) : ''
}

function mesesDe(nascimento: string | null): string {
  const anos = emAnosInteiros(nascimento)
  if (anos === null || anos >= 1) return ''
  const [ano, mes, dia] = nascimento!.split('-').map(Number)
  const hoje = new Date()
  const meses = (hoje.getFullYear() - ano) * 12 + (hoje.getMonth() + 1 - mes) - (hoje.getDate() >= dia ? 0 : 1)
  return String(Math.max(meses, 0))
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

/**
 * "1 de setembro de 2026" — com o ano, que num consentimento é o que importa.
 *
 * `dataPorExtenso` do api.ts diz o dia da semana e cala o ano, que é o certo
 * para o dia de hoje e o errado para uma data de anos atrás. Sai do texto do
 * ISO, sem passar por `Date`: o backend já mandou no fuso da casa.
 */
function emDataLonga(iso: string): string {
  const [ano, mes, dia] = diaDaCasa(iso).split('-').map(Number)
  return `${dia} de ${MESES[mes - 1]} de ${ano}`
}

/**
 * A idade do jeito que a mãe diria. O relógio do aparelho serve: aqui não se
 * marca nada, só se lê — e um dia de diferença não muda "5 anos".
 */
function emAnosInteiros(nascimento: string | null): number | null {
  if (!nascimento) return null
  const [ano, mes, dia] = nascimento.split('-').map(Number)
  const hoje = new Date()
  const jaFezAniversario =
    hoje.getMonth() + 1 > mes || (hoje.getMonth() + 1 === mes && hoje.getDate() >= dia)
  return hoje.getFullYear() - ano - (jaFezAniversario ? 0 : 1)
}

function emAnos(nascimento: string): string {
  const anos = emAnosInteiros(nascimento) ?? 0

  if (anos <= 0) return 'bebê'
  if (anos === 1) return '1 ano'
  return `${anos} anos`
}
